import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import BoltIcon from "@mui/icons-material/Bolt";
import SecurityIcon from "@mui/icons-material/Security";
import { fetchTarjeta, fetchTarjetaById } from "../../actions/tarjeta";
import { cambiarEstadoTarjeta } from "../../actions/dispositivos";
import { useModuloPermissions } from "../../hooks/useModuloPermissions";
import { parserTarjeta } from "../../utils/parsers/tarjeta";
import { usePaginationMUI } from "../../hooks/usePaginationMUI";
import DialogCustom from "../../components/dialog";
import InfoTarjeta from "./info-tarjeta";
import DeviceControlButton from "../../components/device-control-button";

import SensorsIcon from "@mui/icons-material/Sensors";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TableMUI from "../../components/tableMUI";

const iconMap = {
  Proyecto: <TrackChangesIcon fontSize="large" />,
  Sensores: <SensorsIcon fontSize="large" />,
};

const Tarjeta = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogSensor, setOpenDialogSensor] = useState(false);
  const [tarjetaActual, setTarjetaActual] = useState(null);
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const [perPage, setPerPage] = useState(12);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Opciones de perPage - solo múltiplos de 4 hasta 40
  const perPageOptions = [4, 8, 12, 16, 20, 24, 28, 32, 36, 40];

  // Debounce para filtro
  React.useEffect(() => {
    setIsFiltering(true);
    const timeout = setTimeout(() => {
      setDebouncedFilter(filter);
      setIsFiltering(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [filter]);

  const fetchFunction = useMemo(
    () => (perPage, offset) =>
      dispatch(fetchTarjeta(perPage, offset, debouncedFilter)),
    [dispatch, debouncedFilter]
  );

  const { dataSource, total, handlePage, page, isLoading, refreshData } = usePaginationMUI(
    fetchFunction,
    "tarjeta",
    0,
    perPage
  );

  // Hook para permisos de tarjetas
  const permissions = useModuloPermissions('tarjeta');

  // Función para manejar el cambio de estado de la tarjeta
  const handleCambiarEstadoTarjeta = async (tarjetaId, nuevoEstado) => {
    try {
      await dispatch(cambiarEstadoTarjeta(tarjetaId, nuevoEstado));
      // Refrescar datos después del cambio de estado exitoso
      refreshData();
    } catch (error) {
      console.error('Error al cambiar estado de la tarjeta:', error);
      // Re-lanzar el error para que el DeviceControlButton lo maneje
      throw error;
    }
  };

  const pageCount = Math.ceil(total / perPage);

  const handleShowInfo = async (item) => {
    console.log('Item seleccionado:', item); // Para debug
    const result = await dispatch(fetchTarjetaById(item._id));
    console.log('Resultado de fetchTarjetaById:', result); // Para debug
    if (result.payload) {
      console.log('Payload recibido:', result.payload); // Para debug
      setTarjetaActual(result.payload);
      setOpenDialog(true);
    }
  };

  const handleShowSensores = async (item) => {
    
    item.sensores.forEach(s=>{
      if(s.tipo != null || s.tipo != undefined)
        s.tipo = s.tipo.nombre;
    });
    setTarjetaActual(item);
    setOpenDialogSensor(true);
  };

  return (
    <>
      <Box px={2} py={3} sx={{ width: '100%' }}>
        <Box mb={3} display="flex" flexDirection={isMobile ? "column" : "row"} gap={2} justifyContent="center" alignItems="center">
          <TextField
            size="small"
            placeholder="🔍 Filtrar proyecto"
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
            sx={{ maxWidth: 600 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Por página</InputLabel>
            <Select
              value={perPage}
              label="Por página"
              onChange={(e) => setPerPage(e.target.value)}
            >
              {perPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoading || isFiltering ? (
          <Box textAlign="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ width: '100%', px: 1 }}>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(3, 1fr)",
                  xl: "repeat(4, 1fr)",
                }}
                gap={2}
                width="100%"
              >
                {dataSource.map((item) => {
                  const parsed = parserTarjeta(item);

                  return (
                    <Card
                      key={item._id}
                      sx={{
                        height: "100%",
                        backgroundColor: "#303C54",
                        color: "primary.contrastText",
                        borderRadius: 2,
                        boxShadow: 4,
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": { boxShadow: 8 },
                      }}
                    >
                      <CardHeader
                        title={parsed.nombre}
                        action={
                          <>
                            {item.alwaysOn ? (
                              <Tooltip title="Dispositivo Crítico">
                                <IconButton sx={{ color: "orange" }}>
                                  <SecurityIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <></>
                            )}
                            {permissions.puedeControlar && (
                              <DeviceControlButton
                                dispositivo={item}
                                tipo="tarjeta"
                                onCambiarEstado={handleCambiarEstadoTarjeta}
                              />
                            )}
                            {permissions.puedeActualizar && (
                              <IconButton
                                onClick={() => navigate(`/tarjetas/editar/${item._id}`)}
                                sx={{ color: "primary.contrastText" }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={() => handleShowInfo(item)}
                              sx={{ color: "primary.contrastText" }}
                            >
                              <InfoOutlinedIcon />
                            </IconButton>
                          </>
                        }
                        sx={{
                          pb: 0,
                          "& .MuiCardHeader-title": { typography: "h6" },
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1, pt: 1 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="stretch"
                          gap={{ xs: 0.5, sm: 1 }}
                          flexWrap="nowrap"
                          sx={{
                            flexDirection: { xs: "column", sm: "row" }
                          }}
                        >
                          {[
                            { label: "Proyecto", value: parsed.nombre },
                            {
                              label: "Sensores",
                              value: (
                                <Tooltip title="Ver sensores" arrow>
                                  <IconButton
                                    onClick={() => handleShowSensores(item)}
                                    sx={{
                                      backgroundColor: "#1565C0",
                                      color: "#fff",
                                      borderRadius: 2,
                                      width: 36,
                                      height: 36,
                                      transition: "0.3s",
                                      "&:hover": {
                                        backgroundColor: "#3f445c",
                                      },
                                    }}
                                  >
                                    <SensorsIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ),
                            },
                          ].map(({ label, value }) => (
                            <Box
                              key={label}
                              sx={{
                                backgroundColor: "#1F2A38",
                                borderRadius: 1,
                                p: { xs: 1.5, sm: 2 },
                                textAlign: "center",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "space-between",
                                minWidth: { xs: "100%", sm: 100 },
                                height: { xs: 80, sm: 140 },
                                flex: { xs: "none", sm: 1 },
                                mb: { xs: 0.5, sm: 0 }
                              }}
                            >
                              {iconMap[label] || <BoltIcon fontSize={isMobile ? "medium" : "large"} />}
                              <Typography 
                                variant="caption" 
                                mt={0.5}
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                  lineHeight: 1.2
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                variant={isMobile ? "caption" : "body2"}
                                color="white"
                                sx={{ 
                                  fontWeight: "bold",
                                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" }
                                }}
                              >
                                {value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>

            {dataSource.length > 0 && (
              <Box mt={4} display="flex" justifyContent="center">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_e, v) => handlePage(v)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <DialogCustom
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={
          "Detalles de la Tarjeta" +
          (tarjetaActual?.nombre ? " - " + tarjetaActual.nombre : "")
        }
        maxWidth="md"
      >
        <InfoTarjeta tarjeta={tarjetaActual} />
      </DialogCustom>

      <DialogCustom
        open={openDialogSensor}
        onClose={() => setOpenDialogSensor(false)}
        title={
          "Sensores Tarjeta - " + (tarjetaActual ? tarjetaActual.nombre : "")
        }
        maxWidth="lg"
      >
        <TableMUI
          title={"Tarjeta: " + (tarjetaActual ? tarjetaActual.nombre : "")}
          data={tarjetaActual ? tarjetaActual.sensores : []}
          columns={[
            { key: "nombre", header: "Nombre del Sensor" },
            { key: "tipo", header: "Tipo" },
            { key: "unidades", header: "Unidades" },
            { key: "rangoMin", header: "Rango Mínimo" },
            { key: "rangoMax", header: "Rango Máximo" },
            { key: "resolucionMin", header: "Resolución Mínima" },
            { key: "resolucionMax", header: "Resolución Máxima" },
            { key: "peligrosidad", header: "Peligrosidad" },
            { key: "peligrosidadAjuste", header: "Peligrosidad Ajustada" },
          ]}
        />
      </DialogCustom>
    </>
  );
};

export default Tarjeta;
